import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import PropTypes from 'prop-types';
import { Card } from 'react-bootstrap';
import {
    LineChart
} from 'echarts/charts';
import {
    GridComponent,
    TooltipComponent,
    LegendComponent
} from 'echarts/components';
import {
    CanvasRenderer
} from 'echarts/renderers';
import { ChartSkeleton } from './SkeletonLoader';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const AkasiaPosUserActivityChart = ({ activityData, title, onDateClick, filterElement, loading }) => {
    if (loading) {
        return <ChartSkeleton title={title} />;
    }

    const chartData = useMemo(() => {
        if (!activityData || activityData.length === 0) return [];

        // Robust detection for hourly data (supports "10:00" and "dd/MM HH:mm")
        const isHourly = activityData.some(item => item.dFecha && item.dFecha.includes(':') && item.dFecha.length < 16);

        if (isHourly) {
            const hourlyCounts = new Array(24).fill(0);

            activityData.forEach(item => {
                if (!item.dFecha) return;

                // Extract Hour. Handle "10:00" or "03/02 10:00"
                let hourPart = item.dFecha.split(':')[0]; // "10" or "03/02 10"
                if (hourPart.includes(' ')) {
                    hourPart = hourPart.split(' ').pop(); // "10"
                }

                const hour = parseInt(hourPart, 10);
                if (!isNaN(hour) && hour >= 0 && hour < 24) {
                    hourlyCounts[hour] = item.iUsuariosConActividad || 0;
                }
            });

            return hourlyCounts.map((count, index) => ({
                dFecha: `${index.toString().padStart(2, '0')}:00`,
                iUsuariosConActividad: count
            }));
        }

        return [...activityData].sort((a, b) => new Date(a.dFecha) - new Date(b.dFecha));
    }, [activityData]);

    const option = useMemo(() => {
        if (!chartData.length) return {};

        const dates = chartData.map(item => {
            const dStr = item.dFecha || '';
            if (dStr.includes(':') || dStr.includes('/')) return dStr;

            // Parse YYYY-MM-DD as local date to avoid timezone issues
            const [year, month, day] = dStr.split('-').map(Number);
            const localDate = new Date(year, month - 1, day);
            return localDate.toLocaleDateString('es-MX', { month: 'short', day: 'numeric' });
        });

        const counts = chartData.map(item => item.iUsuariosConActividad || 0);

        return {
            tooltip: {
                trigger: 'axis'
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates
            },
            yAxis: {
                type: 'value',
                minInterval: 1
            },
            series: [
                {
                    name: title || 'Usuarios Activos',
                    type: 'line',
                    smooth: true,
                    data: counts,
                    areaStyle: {
                        opacity: 0.1,
                        color: '#00d97e'
                    },
                    itemStyle: {
                        color: '#00d97e'
                    }
                }
            ]
        };
    }, [chartData, title]);

    const onEvents = useMemo(() => ({
        'click': (params) => {
            if (onDateClick && params && chartData[params.dataIndex]) {
                onDateClick(chartData[params.dataIndex].dFecha);
            }
        }
    }), [onDateClick, chartData]);

    if (!activityData || activityData.length === 0) {
        return (
            <Card className="h-100 shadow-sm">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center" style={{ overflow: 'visible' }}>
                    <h6 className="mb-0">{title}</h6>
                    {filterElement && <div onClick={e => e.stopPropagation()}>{filterElement}</div>}
                </Card.Header>
                <Card.Body className="d-flex align-items-center justify-content-center">
                    <p className="text-muted">No activity data available.</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="h-100 shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center" style={{ overflow: 'visible' }}>
                <h6 className="mb-0">{title}</h6>
                {filterElement && <div onClick={e => e.stopPropagation()}>{filterElement}</div>}
            </Card.Header>
            <Card.Body>
                <ReactEChartsCore
                    echarts={echarts}
                    option={option}
                    onEvents={onEvents}
                    style={{ height: '300px', width: '100%' }}
                />
            </Card.Body>
        </Card>
    );
};

AkasiaPosUserActivityChart.propTypes = {
    activityData: PropTypes.array,
    title: PropTypes.string,
    onDateClick: PropTypes.func,
    filterElement: PropTypes.node,
    loading: PropTypes.bool
};

export default AkasiaPosUserActivityChart;
